import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X } from 'lucide-react';
import { portfolioData } from '../data/mock';

const Cinematography = () => {
  const videos = portfolioData.videos;
  const [activeVideo, setActiveVideo] = useState(null);

  const openVideo = (video) => {
    if (video.youtubeId) {
      setActiveVideo(video);
    }
  };

  return (
    <section id="cinematography" data-testid="cinematography-section" className="py-24 md:py-32 lg:py-40 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="mb-16"
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] font-medium text-white/40 mb-4">Motion</p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight leading-tight text-white">
            Cinematography
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {videos.map((video, i) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.25, 1, 0.5, 1] }}
              data-testid={`video-card-${video.id}`}
              className="group relative overflow-hidden cursor-pointer border border-white/5"
              onClick={() => openVideo(video)}
            >
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full aspect-video object-cover img-grayscale"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors duration-500 flex items-center justify-center">
                {video.youtubeId && (
                  <div className="glass rounded-full p-4 md:p-5 group-hover:scale-110 transition-transform duration-500">
                    <Play className="w-6 h-6 md:w-7 md:h-7 text-white fill-white" />
                  </div>
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                <p className="text-sm md:text-base font-light text-white leading-snug">{video.title}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1.5">{video.category}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 md:p-12"
            data-testid="video-player-overlay"
            onClick={() => setActiveVideo(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative w-full aspect-video bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0`}
                  title={activeVideo.title}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="video-player-iframe"
                />
              </div>
              <div className="mt-4 flex items-start justify-between">
                <div>
                  <p className="text-base md:text-lg font-light text-white">{activeVideo.title}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-1">{activeVideo.category}</p>
                </div>
              </div>
            </motion.div>
            <button
              data-testid="video-player-close"
              onClick={() => setActiveVideo(null)}
              className="absolute top-6 right-6 w-10 h-10 border border-white/20 hover:border-white/50 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-white/60" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Cinematography;
